import { useTenant } from "@/hooks/useTenant";
import { useSiteAssetResolver } from "@/hooks/useSiteAsset";
import { resolveShareImage, type ShareImage } from "@/lib/seo";
import { ogPageAssetKey } from "@/lib/siteAssets";
import type { SocialPageId } from "@/lib/siteCopy";

// Cards are stored at 1200×630; ask the transformer for exactly that box so
// every scraper downloads the same small file.
const CARD_OPTS = { width: 1200, height: 630, format: "origin" } as const;

export interface UseShareImageArgs {
  /** Which public page this is, enables the admin's per-page card. */
  social?: SocialPageId;
  /** The page's own image (listing photo, area hero). */
  explicit?: string | null;
  explicitAlt?: string | null;
  explicitDims?: { width: number; height: number; type?: string } | null;
}

/** The share image for the current page, most specific first (resolveShareImage). */
export function useShareImage(args: UseShareImageArgs = {}): ShareImage {
  const tenant = useTenant();
  const resolve = useSiteAssetResolver();
  const pageCard = args.social ? resolve(ogPageAssetKey(args.social), CARD_OPTS) : null;
  const siteCard = resolve("seo.ogImage", CARD_OPTS);
  return resolveShareImage({
    explicit: args.explicit,
    explicitAlt: args.explicitAlt,
    explicitDims: args.explicitDims,
    pageCard: pageCard && !pageCard.isFallback ? pageCard.src : null,
    pageCardAlt: pageCard?.alt,
    uploaded: siteCard.isFallback ? null : siteCard.src,
    uploadedAlt: siteCard.alt,
    tenantUrl: tenant.ogImage,
    siteUrl: tenant.siteUrl,
    siteName: tenant.siteName,
  });
}
